import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { User, Phone, MapPin, Home, Globe, Award, Repeat } from "lucide-react";
import api from "../../lib/api";
import { EmployerTabs } from "../../components/employer/EmployerTabs";
import { LockedFeature } from "../../components/employer/LockedFeature";

export default function EmployerCandidates() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/job-requests").then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const requests = data?.requests || [];
  const limit = data?.limit;
  const total = data?.total || 0;
  const locked = limit === 0;

  const Tag = ({ icon: Icon, label }) => (
    <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-stone-bg text-stone-sub border border-stone-border">
      <Icon className="w-3.5 h-3.5" /> {label}
    </span>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading text-3xl font-bold text-stone-text mb-1">{t("employer.candidates.title")}</h1>
      <p className="text-stone-muted mb-6">{t("employer.candidates.subtitle")}</p>
      <EmployerTabs />

      {loading ? (
        <div className="py-12 text-center text-stone-muted">{t("jobs.loading")}</div>
      ) : locked ? (
        <LockedFeature description={t("employer.locked.candidates")} />
      ) : requests.length === 0 ? (
        <div className="py-12 text-center text-stone-muted" data-testid="ecands-empty">{t("employer.candidates.empty")}</div>
      ) : (
        <>
          <p className="text-sm text-stone-muted mb-4" data-testid="ecands-count">{t("employer.candidates.showing", { shown: requests.length, total })}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="ecands-list">
            {requests.map((r) => (
              <div key={r.id} className="bg-white rounded-2xl border border-stone-border shadow-sm p-5" data-testid="ecand-item">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-brand-soft flex items-center justify-center"><User className="w-5 h-5 text-brand" /></div>
                  <div>
                    <h3 className="font-heading font-semibold text-stone-text">{r.candidate_name}</h3>
                    <p className="text-sm text-stone-sub">{r.desired_position || (r.any_position ? "—" : "")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-xs text-stone-muted mb-2"><MapPin className="w-3.5 h-3.5" /> {r.city}{r.state ? `, ${r.state}` : ""}</div>
                {r.description && <p className="text-sm text-stone-sub mb-3 line-clamp-2">{r.description}</p>}
                <div className="flex flex-wrap gap-2 mb-3">
                  {r.seeks_housing && <Tag icon={Home} label={t("employer.candidates.seeksHousing")} />}
                  {r.has_experience && <Tag icon={Award} label={t("employer.candidates.experience")} />}
                  {r.speaks_english && <Tag icon={Globe} label={t("employer.candidates.english")} />}
                  {r.willing_to_relocate && <Tag icon={Repeat} label={t("employer.candidates.relocate")} />}
                </div>
                {r.phone && <a href={`tel:${r.phone}`} className="flex items-center gap-1 text-sm font-medium text-brand"><Phone className="w-4 h-4" /> {r.phone}</a>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
